import { Dispatch, SetStateAction, useContext, useRef, useState } from 'react';
import { defaultQueryClient } from '@shared/query-client/QueryClient';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Eye } from 'lucide-react';

import { useBackUrl } from '@/App.hooks';
import { AppContext } from '@/App.context';
import {
	ACCESS_TOKEN_EXPIRE_AT_KEY,
	USER_ROLE,
	ACCESS_TOKEN_KEY,
} from '@/constants';

import { Loading } from '@shared/ui/loading/Loading';
import { NavbarIconLogo } from '@shared/ui/navbar-icon-logo';
import { AUTH_KEY } from '@shared/auth/Authentication.hooks';
import { clearPersistStorage } from '@shared/query-client/QueryClient';

import styles from './Drafts.module.css';
import { AuthMode, useAdminAuth } from './Drafts.hooks';

const Drafts = () => {
	return (
		<div className={styles['container']}>
			<View />
		</div>
	);
};

function View() {
	return (
		<div className={styles['view']}>
			<Topbar />
			<Content />
		</div>
	);
}

function Topbar() {
	return (
		<nav className={styles['topbar']}>
			<div>
				<BackButton />
			</div>
			<div className={styles['title']}>
				<NavbarIconLogo />
			</div>
			<div></div>
		</nav>
	);
}

function Content() {
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const [mode, setMode] = useState<AuthMode>(
		localStorage.getItem(AUTH_KEY) ? 'logout' : 'login',
	);
	if (mode === 'logout' || isAdmin) {
		return <Logout setMode={setMode} />;
	}

	return <Login setMode={setMode} />;
}

function Login({ setMode }: AuthProps) {
	const formRef = useRef<HTMLFormElement>(null);
	const { parseGraphQLClientError } = useContext(AppContext);
	const mutation = useAdminAuth({
		setMode,
		onError: (_err) => {
			if (formRef.current) {
				formRef.current.reset();
			}
			setPassword('');
		},
	});

	const [password, setPassword] = useState<string>('');

	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		mutation.mutate({ password });
	};

	if (mutation.isPending) {
		return (
			<div className={styles['content']}>
				<div>
					<Loading />
				</div>
			</div>
		);
	}

	const classNames = [styles['content']];
	if (mutation.isError) {
		classNames.push(styles['error']);
	}

	return (
		<div className={classNames.join(' ')}>
			<form ref={formRef} onSubmit={onSubmit}>
				<h4>{mutation.isError ? 'Error' : 'Editor log in'}</h4>
				<p>
					{mutation.isError
						? parseGraphQLClientError(mutation.error).message
						: 'Request credentials from your Account Manager'}
				</p>
				<input
					required
					onChange={(e) => {
						const { value } = e.target;
						setPassword(value);
					}}
					placeholder="Password"
					value={password}
					title="password"
					type={'password'}
				/>

				<button title="Send">Send</button>
			</form>
		</div>
	);
}

function Logout({ setMode }: AuthProps) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { db } = useContext(AppContext);
	const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const keys = [
			AUTH_KEY,
			USER_ROLE,
			ACCESS_TOKEN_KEY,
			ACCESS_TOKEN_EXPIRE_AT_KEY,
		];
		for (const k of keys) {
			if (localStorage.getItem(k)) {
				localStorage.removeItem(k);
			}
		}
		if (db) {
			await db.clear();
		}
		queryClient.removeQueries();
		await clearPersistStorage();
		defaultQueryClient.clear();
		setMode('login');
	};

	return (
		<div className={styles['content']}>
			<form onSubmit={onSubmit}>
				<h4>You are currently logged in</h4>
				<p></p>
				<button
					onClick={() => {
						navigate({
							to: '/',
							replace: true,
						});
					}}
					type="button"
					style={{ marginBottom: 20 }}
				>
					Go to Home
				</button>
				<button type={'submit'} title="Send">
					Log out
				</button>
				<small>Logging out will clear all stored data</small>
			</form>
		</div>
	);
}

interface AuthProps {
	setMode: Dispatch<SetStateAction<AuthMode>>;
}

function BackButton() {
	const url = useBackUrl();
	return (
		<button id="back-button" type="button" title="Back">
			<Link to={url}>
				<ArrowLeft id={'back-btn'} color={'#333'} />
			</Link>
		</button>
	);
}

export function EyeIndicator() {
	const className = [
		styles['draft-icon'],
		'tooltip down cursor-pointer',
	].join(' ');
	const onClick = () => {
		window.location.replace('/drafts');
	};

	if (localStorage.getItem(USER_ROLE) !== 'admin') {
		return null;
	}

	if (localStorage.getItem(ACCESS_TOKEN_KEY)) {
		return (
			<button
				type="button"
				className={className}
				data-text="Preview Mode"
				onClick={onClick}
			>
				<Eye />
			</button>
		);
	}
	return null;
}

export default Drafts;
